/**
 * HostActions runtime — the offer → {accept, skip, correct} → execute/capture
 * lifecycle plumbing for the frozen Avatar Loop contracts (2.10.0).
 *
 * The TYPES here are frozen (`src/loop/contracts.ts`, 27 Aug 2026); this file
 * implements to them and must not widen them.
 *
 * THE MOTOR BOUNDARY IS ENFORCED AT RUNTIME, not just in the types:
 *
 *   1. `createAvatarActionLifecycle` only calls `HostActions.execute` inside
 *      the accept/correct branch — the skip branch never touches it.
 *   2. `createHostActionRegistry(...).execute` re-validates `verb` before
 *      invoking any handler, so an untyped JS caller (or a forged
 *      `avatar:action:response` event with a fabricated verb) is refused
 *      without the handler ever running. The frozen type already makes skip
 *      inexpressible as an execute request; this is the runtime line behind
 *      that type-level line.
 *   3. The lifecycle executes the OFFERED envelope, not whatever envelope a
 *      response event carries — the echo on the response is for stateless
 *      consumers, never an instruction.
 *   4. Each offer is consumed on first valid response (at-most-once
 *      execution per `offerId`); duplicates and unknown ids are ignored.
 *
 * Generalises `AvatarHostAdapter.performAction` (`src/host/adapter.ts`)
 * additively: `createPerformActionFromHostActions` lets a host satisfy the
 * existing untyped `performAction` from its typed registry. No existing
 * consumer changes.
 */

import type { AvatarHostActionRequest, AvatarHostActionResult, AvatarHostAdapter } from '../host/adapter';
import type {
  AvatarActionCapturedNote,
  AvatarActionCorrection,
  AvatarActionEnvelope,
  AvatarActionExecutedDetail,
  AvatarActionOfferDetail,
  AvatarActionResponseDetail,
  AvatarActionVerb,
  HostActionDefinition,
  HostActionExecuteRequest,
  HostActions,
  MemoryUserFacingNoteType
} from './contracts';
import { addAvatarLoopEventListener, dispatchAvatarLoopEvent } from './events';

/** The three frozen verbs, as a runtime list for validating untyped input. */
export const AVATAR_ACTION_VERBS: readonly AvatarActionVerb[] = [
  'accept',
  'skip',
  'correct'
];

/** Runtime guard for the frozen three-verb union. There is no fourth verb. */
export function isAvatarActionVerb(value: unknown): value is AvatarActionVerb {
  return (AVATAR_ACTION_VERBS as readonly unknown[]).includes(value);
}

function toErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return typeof error === 'string' ? error : 'action handler failed';
}

/**
 * Build a `HostActions` registry from typed definitions. Each definition
 * binds one envelope `name` to one typed handler returning the existing
 * `AvatarHostActionResult` shape.
 *
 * `execute` never throws: unknown actions, invalid verbs, and handler
 * exceptions all surface as `{ ok: false, error }`.
 */
export function createHostActionRegistry(
  definitions: ReadonlyArray<HostActionDefinition<never, unknown>>
): HostActions {
  const actions: Record<string, HostActionDefinition> = {};
  for (const definition of definitions) {
    if (actions[definition.name]) {
      throw new Error(`duplicate host action definition: '${definition.name}'`);
    }
    actions[definition.name] = definition as HostActionDefinition;
  }

  return {
    actions,
    async execute(request: HostActionExecuteRequest): Promise<AvatarHostActionResult> {
      // Runtime motor boundary: the type already excludes 'skip', but an
      // untyped JS caller can still hand us anything. Nothing runs unless
      // the verb is an explicit accept or corrected accept.
      const verb: unknown = request?.verb;
      if (verb !== 'accept' && verb !== 'correct') {
        return {
          ok: false,
          error: `motor boundary: refusing to execute with verb '${String(verb)}' — only 'accept' or 'correct' may execute`
        };
      }

      const name = request?.action?.name;
      const definition = typeof name === 'string' ? actions[name] : undefined;
      if (!definition) {
        return { ok: false, error: `unknown host action: '${String(name)}'` };
      }

      try {
        return await definition.execute(request);
      } catch (error) {
        return { ok: false, error: toErrorMessage(error) };
      }
    }
  };
}

/**
 * Bridge a typed registry back onto the existing untyped
 * `AvatarHostAdapter.performAction` surface, exactly as the frozen contract
 * describes ("a host may satisfy `performAction` by dispatching
 * `request.type` into its registry"). Additive — existing `performAction`
 * consumers keep working unchanged.
 *
 * `performAction` calls are direct host-initiated actions, not offers
 * awaiting a human verb, so they execute as `accept`.
 */
export function createPerformActionFromHostActions(
  hostActions: HostActions
): NonNullable<AvatarHostAdapter['performAction']> {
  return (request: AvatarHostActionRequest): Promise<AvatarHostActionResult> => {
    const payload = request?.payload;
    const args: Record<string, unknown> =
      payload && typeof payload === 'object' && !Array.isArray(payload)
        ? (payload as Record<string, unknown>)
        : payload === undefined
          ? {}
          : { payload };

    return hostActions.execute({
      action: { name: request.type, args },
      verb: 'accept'
    });
  };
}

/**
 * Everything the host's capture step needs to write the visible typed
 * Timeline note for one verb. ALL THREE VERBS capture (a skip answered is
 * information); `result` is present only when the handler actually ran.
 */
export interface AvatarActionCaptureContext {
  offer: AvatarActionOfferDetail;
  verb: AvatarActionVerb;
  /** The human's edits — present iff `verb === 'correct'`. */
  edits?: AvatarActionCorrection;
  /** Handler result — present iff the verb executed (accept/correct). */
  result?: AvatarHostActionResult;
}

/**
 * Host-implemented capture step (typically `MemoryProvider.capture` plus the
 * host's own note-type mapping). Must return the Timeline note written —
 * every verb writes one. If it throws, the lifecycle still emits
 * `avatar:action:executed`, with `captured: null` (§8 clarification).
 */
export type AvatarActionCaptureHandler = (
  context: AvatarActionCaptureContext
) => Promise<AvatarActionCapturedNote> | AvatarActionCapturedNote;

export interface AvatarActionOfferInput {
  /** The action envelope being offered (travels as `detail.envelope`). */
  envelope: AvatarActionEnvelope;
  /** The thin draft / question the human may edit on `correct`. */
  offeredContent?: string;
  /** User-facing type an accept would write to the Timeline. */
  noteType?: MemoryUserFacingNoteType;
  /** Supply your own correlation id; defaults to a generated one. */
  offerId?: string;
}

export interface CreateAvatarActionLifecycleOptions {
  /** The typed registry executing accepted offers. */
  actions: HostActions;
  /** Writes the visible typed Timeline note for every verb. */
  capture: AvatarActionCaptureHandler;
  /**
   * Event target carrying the three lifecycle events. Defaults to `window`;
   * injectable for tests and non-window hosts.
   */
  target?: EventTarget;
  /** Override offer-id generation (defaults to a UUID when available). */
  generateOfferId?: () => string;
  /**
   * Called when a lifecycle pass fails (handler/capture threw, malformed
   * response). Failures never throw out of the event listener.
   */
  onError?: (error: unknown, offerId?: string) => void;
}

export interface AvatarActionLifecycle {
  /**
   * Publish an offer: dispatches `avatar:action:offer` and arms the
   * lifecycle to receive exactly one `avatar:action:response` for it.
   */
  offer(input: AvatarActionOfferInput): AvatarActionOfferDetail;
  /**
   * Apply a human response programmatically (same path the
   * `avatar:action:response` window event takes). Resolves with the
   * `avatar:action:executed` detail, or `undefined` when the response was
   * ignored (unknown/consumed `offerId`, invalid verb). A failed capture
   * still resolves with the detail — carrying `captured: null` (§8).
   */
  respond(detail: AvatarActionResponseDetail): Promise<AvatarActionExecutedDetail | undefined>;
  /** Whether an offer is still awaiting its human response. */
  hasPendingOffer(offerId: string): boolean;
  /** Remove the response listener and drop all pending offers. */
  dispose(): void;
}

function defaultGenerateOfferId(): string {
  const cryptoObj = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID();
  }
  return `offer-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Create the offer → response → executed lifecycle around a `HostActions`
 * registry and a host capture step. See the module header for the runtime
 * motor-boundary guarantees.
 */
export function createAvatarActionLifecycle(
  options: CreateAvatarActionLifecycleOptions
): AvatarActionLifecycle {
  const { actions, capture, generateOfferId = defaultGenerateOfferId, onError } = options;
  const target = options.target ?? window;
  const pendingOffers = new Map<string, AvatarActionOfferDetail>();

  const respond = async (
    detail: AvatarActionResponseDetail
  ): Promise<AvatarActionExecutedDetail | undefined> => {
    if (!detail || typeof detail.offerId !== 'string') {
      return undefined;
    }

    const offer = pendingOffers.get(detail.offerId);
    if (!offer) {
      // Unknown or already-consumed offer — at-most-once execution.
      return undefined;
    }

    if (!isAvatarActionVerb(detail.verb)) {
      // A fabricated verb is NOT an accept. Leave the offer pending so a
      // real response can still arrive.
      onError?.(
        new Error(`invalid avatar action verb: '${String(detail.verb)}'`),
        detail.offerId
      );
      return undefined;
    }

    // Consume the offer BEFORE executing so a duplicate response can never
    // re-execute the same offer.
    pendingOffers.delete(detail.offerId);

    const verb = detail.verb;
    const edits = verb === 'correct' ? detail.edits : undefined;

    let result: AvatarHostActionResult | undefined;
    let captured: AvatarActionCapturedNote | null = null;
    try {
      if (verb === 'accept' || verb === 'correct') {
        // The ONLY call site of execute — an explicit accept or corrected
        // accept, on the envelope that was offered (never the echo on the
        // response event).
        result = await actions.execute({ action: offer.envelope, verb, edits });
      }
      // skip: no execution, by construction — this branch has no execute.

      captured = await capture({ offer, verb, edits, result });
    } catch (error) {
      // Capture failed: the pass still ends with the frozen executed event,
      // carrying `captured: null` (clarification ruled 27 Aug 2026,
      // AVATAR_LOOP_CONTRACTS.md §8 — the shipped Decision Memory wire).
      // The error also surfaces to the host.
      captured = null;
      onError?.(error, detail.offerId);
    }

    // The frozen executed wire: EXACTLY { offerId, verb, executed, captured }
    // (§2 table / §6 decision #3) — no envelope echo, no handler-result
    // field. The handler result reached the capture step above; it never
    // travels on the event.
    const executedDetail: AvatarActionExecutedDetail = {
      offerId: offer.offerId,
      verb,
      executed: verb !== 'skip',
      captured
    };

    dispatchAvatarLoopEvent('avatar:action:executed', executedDetail, target);
    return executedDetail;
  };

  const removeResponseListener = addAvatarLoopEventListener(
    'avatar:action:response',
    (detail) => {
      void respond(detail);
    },
    target
  );

  return {
    offer(input: AvatarActionOfferInput): AvatarActionOfferDetail {
      const offerDetail: AvatarActionOfferDetail = {
        offerId: input.offerId ?? generateOfferId(),
        envelope: input.envelope,
        ...(input.offeredContent !== undefined ? { offeredContent: input.offeredContent } : {}),
        ...(input.noteType !== undefined ? { noteType: input.noteType } : {})
      };
      pendingOffers.set(offerDetail.offerId, offerDetail);
      dispatchAvatarLoopEvent('avatar:action:offer', offerDetail, target);
      return offerDetail;
    },
    respond,
    hasPendingOffer(offerId: string): boolean {
      return pendingOffers.has(offerId);
    },
    dispose(): void {
      removeResponseListener();
      pendingOffers.clear();
    }
  };
}
